import React, { FC, useEffect, useMemo, useState } from 'react';
import Button from '@ingka/button';
import Text from '@ingka/text';
import Stack from '@mui/material/Stack';
import { DataGridPremium, GridColDef } from '@mui/x-data-grid-premium';
import { useDispatch } from 'react-redux';
import {
  changeSelectedFy,
  updateCurrency,
} from '../../store/currency/actionWrappers';
import { useTypedSelector } from '../../store';
import {
  selectCurrencyFactorsLoading,
  selectCurrencyFys,
  selectSelectedCurrency,
  selectSelectedFy,
} from '../../store/currency/selectors';
import useTranslation from '../../hooks/useTranslation';
import { FormControl, InputLabel, MenuItem, Select } from '@mui/material';
import { getFY, getFyNumber } from '../../utils/calculation';
import LoadingScreen from '../../common/LoadingScreen';
import { getCurrencyTableColumns } from './utils';

const CurrencyPage: FC = () => {
  const dispatch = useDispatch();
  const t = useTranslation();
  const currencies = useTypedSelector(selectSelectedCurrency);
  const fys = useTypedSelector(selectCurrencyFys);
  const selectedFy = useTypedSelector(selectSelectedFy);
  const loading = useTypedSelector(selectCurrencyFactorsLoading);
  const [fyState, setFyState] = useState<string>('');
  const [tableState, setStableState] = useState([]);
  const [paginationModel, setPaginationModel] = React.useState({
    pageSize: 10,
    page: 0,
  });
  const editable = getFyNumber(getFY()) <= getFyNumber(fyState);

  const fyChangedHandler = (payload) => {
    setFyState(payload);
    dispatch(changeSelectedFy(payload));
  };

  useEffect(() => {
    setFyState(selectedFy || fys[0] || '');
  }, [fys]);

  const updateCurrencyHandler = () => {
    dispatch(
      updateCurrency({
        factors: Object.values(tableState),
        fy: selectedFy,
      }),
    );
  };

  const columns: GridColDef[] = useMemo(() => {
    return getCurrencyTableColumns(editable, t);
  }, [editable, t]);
  return (
    <Stack padding={2}>
      {loading && <LoadingScreen message="Loading Currencies" />}
      {!loading && (
        <>
          <Stack spacing={3}>
            <Text tagName="h1" textStyle="Heading.L">
              {t('currency.currencyPage')}
            </Text>
          </Stack>
          <FormControl
            fullWidth
            sx={{
              width: '100px',
              marginBottom: '20px',
              marginTop: '10px',
            }}
          >
            <InputLabel id="FY-select-label">{t('common.fy')}</InputLabel>
            <Select
              id={'sustain-page-FY'}
              labelId="FY-select-label"
              label="FY"
              value={fyState}
              onChange={(e) => fyChangedHandler(e.target.value)}
            >
              {fys?.map((fy) => (
                <MenuItem value={fy} key={fy}>
                  {fy}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <DataGridPremium
            rows={currencies}
            onStateChange={(e) => {
              setStableState(e.rows.dataRowIdToModelLookup);
            }}
            columns={columns}
            autoHeight
            disableRowSelectionOnClick
            disableColumnMenu
            paginationModel={paginationModel}
            onPaginationModelChange={(newModel) => setPaginationModel(newModel)}
            pageSizeOptions={[10, 20, 40]}
            pagination
          />
        </>
      )}
      {editable && (
        <Stack direction={'row-reverse'} spacing={3} padding={2}>
          <Button
            style={{ paddingBottom: '10px' }}
            type="primary"
            onClick={updateCurrencyHandler}
          >
            {t('currency.updateCurrencyRates')}
          </Button>
        </Stack>
      )}
    </Stack>
  );
};

export default CurrencyPage;
